
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { storage } from '@/firebase';
import { RunnitsImageSelectMixin } from '@/mixins';
import { NODE_RUN_RESULT_SELECTION_MODE } from './constants';
import {
	getRunnitNodeRunResultFilePath,
	getRunnitUploadFilePath,
	RUNNIT_NODE_RUN_RESULT_TYPE,
	RunnitNodeRunResult,
	RunnitUpload,
	THUMB_SIZE,
} from '@run-diffusion/shared';
import { getAsset3DExtension } from '@/views/Runnits/RunnitSettings/Asset3D/asset3DFormats';
import {
	Asset3DViewerHandle,
	createAsset3DViewer,
} from '@/views/Runnits/RunnitSettings/Asset3D/babylonAsset3D';
import {
	GALLERY_SELECTION_CONTEXT_KEY,
	isGalleryMultiSelection,
	isGallerySelectionMode,
	isGallerySingleSelection,
	isSelectedInGalleryContext,
} from '@/components/ImageGallery/gallerySelectionContext';

const ASSET_3D_RESULT_VIEWER_ROTATION_SENSIBILITY = 6000;
const ASSET_3D_RESULT_VIEWER_PANNING_SENSIBILITY = 2400;
const ASSET_3D_RESULT_VIEWER_WHEEL_DELTA_PERCENTAGE = 0.004;

export default Vue.extend({
	name: 'RunnitAsset3D',
	inject: {
		gallerySelectionContext: { from: GALLERY_SELECTION_CONTEXT_KEY, default: null },
	},
	mixins: [RunnitsImageSelectMixin],
	props: {
		nodeRunResult: { type: Object as () => RunnitNodeRunResult | null, default: null },
		upload: { type: Object as () => RunnitUpload | null, default: null },
		interactive: { type: Boolean, default: false },
		isThumb: { type: Boolean, default: false },
		noClick: { type: Boolean, default: false },
		clearable: { type: Boolean, default: false },
		width: { type: [String, Number], default: null },
		height: { type: [String, Number], default: null },
		maxHeight: { type: [String, Number], default: null },
		isSelectedImage: { type: Boolean, default: false },
		draggable: { type: Boolean, default: false },
		isDragging: { type: Boolean, default: false },
	},
	data () {
		return {
			displaySrc: null as string | null,
			srcIsError: false,
			// Bumped on every refresh/dispose so stale in-flight async loads
			// (display URL fetch, interactive viewer creation) are discarded
			// instead of clobbering the current asset or leaking a Babylon engine.
			loadToken: 0,
			viewer: null as Asset3DViewerHandle | null,
			dragTimeout: null as any,
		};
	},
	computed: {
		...mapState([
			'dragAndDrop',
		]),
		hasActiveDropZones (): boolean {
			return this.dragAndDrop.activeDropZones > 0;
		},
		// Interactive viewers own the pointer for orbit/pan, so dragging the card
		// out of one would fight the camera controls.
		canBeDragged (): boolean {
			return this.draggable && this.hasActiveDropZones && !this.interactive;
		},
		assetName (): string {
			return this.nodeRunResult?.file?.name || this.upload?.name || '3d-asset';
		},
		assetExtensionLabel (): string {
			const ext = getAsset3DExtension(this.assetName);
			return ext ? ext.toUpperCase() : '3D';
		},
		containerStyle (): Record<string, string | number> {
			return {
				width: this.width || '100%',
				height: this.height || '100%',
				...(this.maxHeight ? { maxHeight: this.maxHeight } : {}),
			};
		},
		isSelectionMode (): boolean {
			if (this.gallerySelectionContext) {
				return isGallerySelectionMode(this.gallerySelectionContext, RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D, this.noClick);
			}
			return !!(!this.noClick && this.currentSelectionState.nodeRunResultSelectionMode && this.currentSelectionState.canSelectTypes.includes(RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D));
		},
		isSingleSelection (): boolean {
			if (this.gallerySelectionContext) {
				return isGallerySingleSelection(this.gallerySelectionContext);
			}
			return this.currentSelectionState.nodeRunResultSelectionMode === NODE_RUN_RESULT_SELECTION_MODE.SINGLE;
		},
		isMultiSelection (): boolean {
			if (this.gallerySelectionContext) {
				return isGalleryMultiSelection(this.gallerySelectionContext);
			}
			return this.currentSelectionState.nodeRunResultSelectionMode === NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE;
		},
		isSelected (): boolean {
			if (this.gallerySelectionContext) {
				return isSelectedInGalleryContext(this.gallerySelectionContext, {
					nodeRunResultUuid: this.nodeRunResult?.uuid || null,
					uploadId: this.upload?.id || null,
				});
			}
			return !!(
				this.nodeRunResult &&
				this.currentSelectionState.selectedNodeRunResults &&
				this.currentSelectionState.selectedNodeRunResults.some((result: RunnitNodeRunResult) => result.uuid === this.nodeRunResult?.uuid)
			);
		},
	},
	watch: {
		nodeRunResult () {
			this.refreshAsset();
		},
		upload () {
			this.refreshAsset();
		},
		interactive () {
			this.refreshAsset();
		},
	},
	mounted () {
		this.refreshAsset();
	},
	beforeDestroy () {
		this.disposeViewer();
		this.clearDragTimeout();
	},
	methods: {
		...mapActions([
			'setIsDragging',
			'setDraggedItem',
		]),
		clearDragTimeout (): void {
			if (this.dragTimeout) {
				clearTimeout(this.dragTimeout);
				this.dragTimeout = null;
			}
		},
		handleDragStart (ev): void {
			if (!this.canBeDragged) return;

			// The static thumbnail is the only meaningful preview; an interactive
			// canvas cannot be handed to setDragImage.
			if (this.displaySrc && !this.srcIsError) {
				const dragPreview = document.createElement('div');
				dragPreview.style.width = '100px';
				dragPreview.style.height = '100px';
				dragPreview.style.borderRadius = '6px';
				dragPreview.style.overflow = 'hidden';
				dragPreview.style.position = 'absolute';
				dragPreview.style.top = '-1000px';

				const previewImg = document.createElement('img');
				previewImg.src = this.displaySrc;
				previewImg.style.width = '100%';
				previewImg.style.height = '100%';
				previewImg.style.objectFit = 'cover';

				dragPreview.appendChild(previewImg);
				document.body.appendChild(dragPreview);
				ev.dataTransfer.setDragImage(dragPreview, 50, 50);
				setTimeout(() => {
					if (dragPreview.parentNode) document.body.removeChild(dragPreview);
				}, 0);
			}

			// RUNNIT_IMAGE is the store's label for any gallery drag, not a claim
			// that this is an image; the ASSET_3D payload below is the discriminator.
			const dragData: any = {
				type: 'RUNNIT_IMAGE',
				src: this.displaySrc || null,
			};
			if (this.nodeRunResult) dragData.nodeRunResult = this.nodeRunResult;
			if (this.upload) dragData.upload = this.upload;

			this.setDraggedItem(dragData);
			this.setIsDragging(true);

			this.dragTimeout = setTimeout(() => {
				this.setIsDragging(false);
				this.setDraggedItem(null);
				document.body.style.cursor = '';
			}, 15000);
		},
		handleDragEnd (): void {
			document.body.style.cursor = '';
			this.clearDragTimeout();
			this.setIsDragging(false);
			this.setDraggedItem(null);
		},
		refreshAsset (): void {
			this.displaySrc = null;
			this.srcIsError = false;
			this.disposeViewer();
			this.$nextTick(() => {
				if (this.interactive) {
					// The interactive canvas emits no <img> load event; 3D previews are
					// always square, so report that for layout parity with the static path.
					this.$emit('dimensions-updated', { width: THUMB_SIZE, height: THUMB_SIZE });
					this.setupInteractiveViewer();
				} else {
					this.fetchDisplaySrc();
				}
			});
		},
		async fetchDisplaySrc (): Promise<void> {
			// The static preview is the derived-path thumbnail generated alongside the
			// model (the generateAsset3DPreview Firebase function for results, the
			// client-side render for uploads) — same `thumbFilePath` convention as
			// images/videos.
			let filePath: string | null = null;
			if (this.nodeRunResult?.file) {
				filePath = getRunnitNodeRunResultFilePath(this.nodeRunResult).thumbFilePath;
			} else if (this.upload?.name) {
				filePath = getRunnitUploadFilePath(this.upload).thumbFilePath;
			}
			if (!filePath) return;

			const loadToken: number = this.loadToken;
			try {
				const url: string = await storage.ref(filePath).getDownloadURL();
				if (loadToken !== this.loadToken) return; // a newer asset started loading — discard
				this.displaySrc = url;
			} catch (err) {
				if (loadToken !== this.loadToken) return;
				console.error('Error fetching ASSET_3D display file URL:', err);
				this.srcIsError = true;
			}
		},
		async fetchOriginalUrl (): Promise<string | null> {
			let filePath: string | null = null;
			if (this.nodeRunResult?.file) {
				filePath = getRunnitNodeRunResultFilePath(this.nodeRunResult).filePath;
			} else if (this.upload?.name) {
				filePath = getRunnitUploadFilePath(this.upload).filePath;
			}
			if (!filePath) return null;
			return await storage.ref(filePath).getDownloadURL();
		},
		async setupInteractiveViewer (): Promise<void> {
			const canvas = this.$refs.renderCanvas as HTMLCanvasElement | undefined;
			if (!this.interactive || !canvas) return;

			const loadToken: number = this.loadToken;
			try {
				const url = await this.fetchOriginalUrl();
				if (!url || loadToken !== this.loadToken) return;
				const response = await fetch(url);
				const blob = await response.blob();
				if (loadToken !== this.loadToken) return;
				const file = new File([blob], this.assetName, { type: blob.type || 'application/octet-stream' });
				const viewer = await createAsset3DViewer({
					canvas,
					file,
					extension: getAsset3DExtension(this.assetName),
					transparentBackground: false,
					backgroundColor: '#1a1a1a',
					cameraName: 'asset3d-result-camera',
					rotationSensibility: ASSET_3D_RESULT_VIEWER_ROTATION_SENSIBILITY,
					panningSensibility: ASSET_3D_RESULT_VIEWER_PANNING_SENSIBILITY,
					wheelDeltaPercentage: ASSET_3D_RESULT_VIEWER_WHEEL_DELTA_PERCENTAGE,
				});
				if (loadToken !== this.loadToken) {
					// Component was destroyed or moved to another asset while we were
					// loading — dispose the just-created engine instead of leaking it.
					viewer.dispose();
					return;
				}
				this.viewer = viewer;
			} catch (err) {
				console.error('Error loading interactive ASSET_3D viewer:', err);
				this.srcIsError = true;
			}
		},
		disposeViewer (): void {
			// Invalidate any in-flight fetchDisplaySrc / setupInteractiveViewer.
			this.loadToken++;
			if (this.viewer) this.viewer.dispose();
			this.viewer = null;
		},
		onImageLoad (event: Event): void {
			const img = event.target as HTMLImageElement;
			this.$emit('dimensions-updated', {
				width: img.naturalWidth,
				height: img.naturalHeight,
			});
		},
		onImageError (): void {
			this.srcIsError = true;
		},
		onClick (): void {
			if (this.noClick) return;
			if (this.isSelectionMode) {
				const payload = {
					nodeRunResult: this.nodeRunResult,
					upload: this.upload,
					avatar: null,
				};
				if (this.isSingleSelection) {
					this.$emit('on-single-select', payload);
				} else if (this.isMultiSelection) {
					this.$emit('on-multi-select', payload);
				}
				return;
			}
			this.$emit('on-click');
		},
	},
});
